import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const connectionString = (process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING ?? '').trim();
const clientName = (process.env.REACT_APP_CLIENT_NAME ?? 'frontend').trim() || 'frontend';
const environmentName = (process.env.REACT_APP_ENV ?? 'development').trim() || 'development';
const hasLikelyAppInsightsConnectionString =
  /InstrumentationKey=/i.test(connectionString) || /IngestionEndpoint=/i.test(connectionString);
const defaultTelemetryProperties = {
  clientName,
  source: clientName,
  environment: environmentName,
};

let appInsights: ApplicationInsights | null = null;
const aiDebugEnabled = () => localStorage.getItem('dewey_ai_debug') === '1';

const withDefaultProperties = (properties?: { [key: string]: string | number | boolean }) => ({
  ...defaultTelemetryProperties,
  ...(properties ?? {}),
});

if (connectionString && hasLikelyAppInsightsConnectionString) {
  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      disableFetchTracking: false,
    },
  });

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((envelope) => {
    const tags = (envelope.tags ?? {}) as Record<string, string>;
    tags['ai.cloud.role'] = clientName;
    tags['ai.cloud.roleInstance'] = window.location.hostname;
    envelope.tags = tags;

    const baseData = (envelope.baseData ?? {}) as { properties?: { [key: string]: string | number | boolean } };
    baseData.properties = withDefaultProperties(baseData.properties);
    envelope.baseData = baseData;
  });

  if (aiDebugEnabled()) {
    console.log('[AI debug] initialized App Insights', {
      hasConnectionString: true,
      hasLikelyAppInsightsConnectionString,
      clientName,
      environmentName,
    });
  }
} else if (connectionString) {
  console.warn('Application Insights is configured, but the connection string appears invalid.');
  if (aiDebugEnabled()) {
    console.log('[AI debug] App Insights NOT initialized: invalid-looking connection string');
  }
} else if (aiDebugEnabled()) {
  console.log('[AI debug] App Insights NOT initialized: REACT_APP_APPINSIGHTS_CONNECTION_STRING is empty at build/runtime');
}

export const trackPageLoad = (properties?: { [key: string]: string | number | boolean }) => {
  if (aiDebugEnabled()) {
    // Toggle by running localStorage.setItem('dewey_ai_debug', '1') in DevTools.
    console.log('[AI debug] trackPageLoad called', { hasAppInsights: !!appInsights, properties });
  }
  appInsights?.trackPageView({
    name: 'page_load',
    uri: window.location.href,
    properties: withDefaultProperties(properties),
  });
  appInsights?.flush();
};

export const trackEvent = (
  name: string,
  properties?: { [key: string]: string | number | boolean },
) => {
  appInsights?.trackEvent({ name }, withDefaultProperties(properties));
};

export default appInsights;
